package fr.frux.core.designsystem.icons

import androidx.compose.ui.graphics.vector.ImageVector

/**
 * Résout le glyphe espèce (`FruxSpeciesGlyphs`) affichable en vignette de liste (#198 : `TreeCard`,
 * `NearbyTreeRow`), à partir du nom scientifique et, en repli, de la partie récoltée.
 *
 * `core-designsystem` ne dépend pas de `:shared` (règle « core independent »), donc pas d'accès à
 * l'enum domaine `HarvestPart` ici : d'où la signature en `String?` (l'appelant passe
 * `harvestPart?.name`). `feature-map` délègue à [speciesGlyphKey] pour les marqueurs de carte, il
 * n'y a plus qu'une seule table de correspondance (cf. #87).
 *
 * @param harvestPartName Nom brut de l'enum domaine `HarvestPart` (`harvestPart?.name`), ou null.
 * @return null si aucune correspondance (ni genre reconnu, ni repli par partie) — l'appelant garde
 * alors son fallback actuel (icône `Park`).
 */
fun glyphFor(
    scientificName: String,
    harvestPartName: String?,
): ImageVector? {
    val key = speciesGlyphKey(scientificName, harvestPartName) ?: return null
    return FruxSpeciesGlyphs.all[key]
}

/**
 * Clé de glyphe (`FruxSpeciesGlyphs.all`) d'une espèce. Fonction pure, sans dépendance au thème.
 *
 * Priorité : genre + épithète (cas spécifiques) > genre seul > repli par partie récoltée. La
 * comparaison est insensible à la casse ; les variétés (ex. "Malus domestica 'Golden'") retombent
 * sur la règle du genre, un 3e token ne change rien.
 *
 * Règle produit : **un glyphe par fruit, pas par genre**. Hors saison la pastille est grise et le
 * glyphe est le seul repère, donc pêcher, prunier et griottier ne doivent pas partager un dessin.
 * Partages volontaires : noyer / noyer noir, mûrier / ronce, cassis / aubépine (grappe de baies),
 * courgette / potiron.
 */
@Suppress("CyclomaticComplexMethod", "LongMethod") // table de décision à plat, une branche par espèce
fun speciesGlyphKey(
    scientificName: String,
    harvestPartName: String?,
): String? {
    val tokens = scientificName.trim().split(' ').filter { it.isNotBlank() }
    val genus = tokens.getOrNull(0)
    val epithet = tokens.getOrNull(1)

    fun fallbackGlyphKey(): String? =
        when (harvestPartName) {
            "FRUIT" -> FruxSpeciesGlyphs.KEY_FRUIT
            "FLEUR" -> FruxSpeciesGlyphs.KEY_FLEUR
            "FEUILLE" -> FruxSpeciesGlyphs.KEY_FEUILLE
            "NOIX" -> FruxSpeciesGlyphs.KEY_COQUE
            "RACINE" -> FruxSpeciesGlyphs.KEY_RACINE
            else -> null
        }

    if (genus == null) return fallbackGlyphKey()

    fun genusIs(name: String) = genus.equals(name, ignoreCase = true)

    fun epithetIs(name: String) = epithet.equals(name, ignoreCase = true)

    return when {
        // Pommes & poires
        genusIs("Malus") -> {
            FruxSpeciesGlyphs.KEY_POMME
        }

        genusIs("Pyrus") -> {
            FruxSpeciesGlyphs.KEY_POIRE
        }

        genusIs("Cydonia") -> {
            FruxSpeciesGlyphs.KEY_COING
        }

        // Nèfle d'Allemagne (Mespilus germanica, reclassée Crataegus germanica) : avant le repli Crataegus.
        genusIs("Mespilus") -> {
            FruxSpeciesGlyphs.KEY_NEFLE
        }

        genusIs("Crataegus") && epithetIs("germanica") -> {
            FruxSpeciesGlyphs.KEY_NEFLE
        }

        genusIs("Cormus") || genusIs("Sorbus") -> {
            FruxSpeciesGlyphs.KEY_SORBE
        }

        genusIs("Eriobotrya") -> {
            FruxSpeciesGlyphs.KEY_NEFLE_JAPON
        }

        // Prunus : chaque fruit son glyphe, le repli « prune » ne sert qu'aux pruniers.
        genusIs("Prunus") && (epithetIs("avium") || epithetIs("cerasus")) -> {
            FruxSpeciesGlyphs.KEY_CERISE
        }

        genusIs("Prunus") && epithetIs("persica") -> {
            FruxSpeciesGlyphs.KEY_PECHE
        }

        genusIs("Prunus") && epithetIs("armeniaca") -> {
            FruxSpeciesGlyphs.KEY_ABRICOT
        }

        genusIs("Prunus") && epithetIs("cerasifera") -> {
            FruxSpeciesGlyphs.KEY_PETITES_PRUNES
        }

        genusIs("Prunus") && epithetIs("spinosa") -> {
            FruxSpeciesGlyphs.KEY_PRUNELLE
        }

        genusIs("Prunus") && epithetIs("dulcis") -> {
            FruxSpeciesGlyphs.KEY_AMANDE
        }

        genusIs("Prunus") -> {
            FruxSpeciesGlyphs.KEY_PRUNE
        }

        // Figues & fruits du sud
        genusIs("Ficus") -> {
            FruxSpeciesGlyphs.KEY_FIGUE
        }

        genusIs("Olea") -> {
            FruxSpeciesGlyphs.KEY_OLIVE
        }

        genusIs("Diospyros") -> {
            FruxSpeciesGlyphs.KEY_KAKI
        }

        genusIs("Punica") -> {
            FruxSpeciesGlyphs.KEY_GRENADE
        }

        genusIs("Arbutus") -> {
            FruxSpeciesGlyphs.KEY_ARBOUSE
        }

        // Fruits à coque
        genusIs("Juglans") -> {
            FruxSpeciesGlyphs.KEY_NOIX
        }

        genusIs("Corylus") -> {
            FruxSpeciesGlyphs.KEY_NOISETTE
        }

        genusIs("Castanea") -> {
            FruxSpeciesGlyphs.KEY_CHATAIGNE
        }

        // Petits fruits
        genusIs("Rubus") && epithetIs("idaeus") -> {
            FruxSpeciesGlyphs.KEY_FRAMBOISE
        }

        genusIs("Rubus") || genusIs("Morus") -> {
            FruxSpeciesGlyphs.KEY_MURE
        }

        genusIs("Ribes") && epithetIs("rubrum") -> {
            FruxSpeciesGlyphs.KEY_GROSEILLE
        }

        genusIs("Ribes") -> {
            FruxSpeciesGlyphs.KEY_GRAPPE_BAIES
        }

        genusIs("Vaccinium") -> {
            FruxSpeciesGlyphs.KEY_MYRTILLE
        }

        genusIs("Rosa") -> {
            FruxSpeciesGlyphs.KEY_CYNORHODON
        }

        genusIs("Fragaria") -> {
            FruxSpeciesGlyphs.KEY_FRAISE
        }

        genusIs("Vitis") -> {
            FruxSpeciesGlyphs.KEY_RAISIN
        }

        // Sureau et aubépine : la fleur se cueille autant que la baie, le glyphe suit la partie.
        genusIs("Sambucus") || genusIs("Crataegus") -> {
            if (harvestPartName == "FLEUR") {
                FruxSpeciesGlyphs.KEY_OMBELLE
            } else {
                FruxSpeciesGlyphs.KEY_GRAPPE_BAIES
            }
        }

        // Aromates : brin pour les tiges ligneuses ou fines, feuille pour le reste.
        genusIs("Thymus") -> {
            FruxSpeciesGlyphs.KEY_BRIN
        }

        genusIs("Salvia") && epithetIs("rosmarinus") -> {
            FruxSpeciesGlyphs.KEY_BRIN
        }

        genusIs("Allium") && epithetIs("schoenoprasum") -> {
            FruxSpeciesGlyphs.KEY_BRIN
        }

        genusIs("Salvia") ||
            genusIs("Laurus") ||
            genusIs("Mentha") ||
            genusIs("Allium") ||
            genusIs("Urtica") -> {
            FruxSpeciesGlyphs.KEY_FEUILLE
        }

        genusIs("Taraxacum") -> {
            FruxSpeciesGlyphs.KEY_FLEUR
        }

        // Potager
        genusIs("Cucurbita") -> {
            FruxSpeciesGlyphs.KEY_COURGE
        }

        genusIs("Daucus") -> {
            FruxSpeciesGlyphs.KEY_RACINE
        }

        genusIs("Raphanus") -> {
            FruxSpeciesGlyphs.KEY_RADIS
        }

        genusIs("Lactuca") -> {
            FruxSpeciesGlyphs.KEY_SALADE
        }

        genusIs("Phaseolus") -> {
            FruxSpeciesGlyphs.KEY_GOUSSE
        }

        else -> {
            fallbackGlyphKey()
        }
    }
}
